import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import ModalListaClientes from '../components/ModalListaClientes'
import BuscadorCliente from '../components/BuscadorCliente'
import HistorialCrm from '../components/HistorialCrm'
import { clientesApi } from '../api/notasVenta'
import { descargarArchivo } from '../utils/descargar'
import { colors, text, space, sx, font, radius } from '../design/tokens'

/**
 * CLIENTES → Historia de clientes — cta cte del cliente desde Presea.
 * Se lee EN VIVO de Presea: la historia con DETALLE_HISTORIA y, al hacer
 * click en una fila, los renglones con "DETALLE_HISTORIA MOV" (cliente + número).
 *
 * Los REMITO planos (mercadería entregada, no facturada) se muestran pero
 * NO suman al saldo ni a los totales — el saldo es deuda financiera real.
 */
export default function HistoriaClientePage() {
  const location = useLocation()
  const [cliente, setCliente] = useState(null)
  const [pickerAbierto, setPickerAbierto] = useState(false)
  const [desde, setDesde] = useState(defaultDesde())
  const [hasta, setHasta] = useState(defaultHasta())
  const [presetActivo, setPresetActivo] = useState(null)
  const [resp, setResp] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [detalle, setDetalle] = useState(null) // { fila, renglones, totales, loading, error }
  const [bajando, setBajando] = useState(null)  // formato en curso
  // Dos solapas sobre el mismo cliente y rango (Martin, 31 oct 2026): la cuenta
  // corriente de Presea y el historial orientado a CRM (ClienteHistorialService).
  const [vista, setVista] = useState(location.state?.vista === 'crm' ? 'crm' : 'presea')
  const [consultaCrm, setConsultaCrm] = useState(0)

  const consultar = async (cli = cliente, d = desde, h = hasta, v = vista) => {
    setError(null)
    if (!cli) return setError('Elegí un cliente.')
    if (!validFecha(d) || !validFecha(h)) return setError('Fechas en formato DD/MM/AAAA.')
    // El historial CRM se carga solo (HistorialCrm): acá solo se le avisa que consulte.
    if (v === 'crm') { setConsultaCrm(n => n + 1); return }
    setLoading(true)
    try {
      const { data } = await clientesApi.historiaPresea(cli.id, d, h)
      setResp(data)
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'No se pudo consultar.')
      setResp(null)
    } finally {
      setLoading(false)
    }
  }

  const elegirCliente = (c) => {
    setCliente(c)
    setPickerAbierto(false)
    setResp(null)
  }

  // Llegada desde el ⌘K (búsqueda global) o desde la NV ("📜 historia del
  // último año"): cliente pre-elegido por router state, con rango opcional.
  useEffect(() => {
    const c = location.state?.cliente
    if (c) {
      const d = validFecha(location.state?.desde) ? location.state.desde : desde
      const h = validFecha(location.state?.hasta) ? location.state.hasta : hasta
      setCliente(c)
      setDesde(d); setHasta(h)
      consultar(c, d, h)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Presets de rango: setean fechas y consultan de una.
  const aplicarPreset = (key) => {
    const { d, h } = rangoPreset(key)
    setDesde(d); setHasta(h); setPresetActivo(key)
    if (cliente) consultar(cliente, d, h)
  }

  const abrirDetalle = async (fila) => {
    setDetalle({ fila, renglones: [], totales: null, cabecera: null, loading: true })
    try {
      const { data } = await clientesApi.renglonesComprobante(cliente.id, fila.nro_comprobante, fila.concepto)
      setDetalle(d => ({ ...d, renglones: data.renglones || [], totales: data.totales, cabecera: data.renglones?.length ? data.cabecera : null, recibo: data.recibo || null, loading: false }))
    } catch (err) {
      setDetalle(d => ({ ...d, renglones: [], loading: false, error: err.response?.data?.error || 'No se pudo traer el detalle.' }))
    }
  }

  const bajarReporte = async (formato) => {
    if (!cliente || !resp) return
    setBajando(formato)
    try {
      const ext = formato === 'excel' ? 'xls' : formato
      await descargarArchivo(
        clientesApi.historiaExportUrl(cliente.id, desde, hasta, formato),
        `cta_cte_${cliente.codigo_presea}_${desde.replaceAll('/', '')}_${hasta.replaceAll('/', '')}.${ext}`,
      )
    } catch {
      setError('No se pudo generar el reporte.')
    } finally {
      setBajando(null)
    }
  }

  const filas = resp?.filas || []

  // Saldo acumulado fila a fila (arranca en saldo_anterior; los remitos no lo mueven)
  // + totales financieros (sin remitos). Cálculo en función pura (fuera del render).
  const { filasSaldo, totales } = useMemo(() => computarSaldos(resp), [resp])

  // Toda la consulta como texto (cabecera + cada movimiento + totales), para
  // mandar por WhatsApp/mail genérico (vos elegís el destinatario).
  const consultaTexto = () => {
    if (!cliente || !resp) return ''
    const L = [
      `CUENTA CORRIENTE — ${cliente.nombre} (Cód. ${cliente.codigo_presea})`,
      `Período ${desde} a ${hasta}`,
      `Saldo anterior: $ ${fmtAR(totales.anterior)}`,
      '',
    ]
    filasSaldo.forEach(f => {
      const mov = f.debe ? `Debe $ ${fmtAR(f.debe)}` : (f.haber ? `Haber $ ${fmtAR(f.haber)}` : '')
      const saldo = f.es_remito ? ' (remito, no suma)' : ` | Saldo $ ${fmtAR(f.saldo)}`
      L.push(`${f.fecha}  ${f.concepto}${f.nro_comprobante ? ` Nº ${f.nro_comprobante}` : ''}  ${mov}${saldo}`)
    })
    L.push('', `TOTALES — Debe $ ${fmtAR(totales.debe)} · Haber $ ${fmtAR(totales.haber)}`, `SALDO FINAL: $ ${fmtAR(totales.saldoFinal)}`)
    return L.join('\n')
  }

  // WhatsApp genérico: abre el selector de contacto con la consulta ya escrita.
  const enviarWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(consultaTexto())}`, '_blank')

  const enviarMail = () => {
    const asunto = `Cuenta corriente — ${cliente.nombre} (${desde} a ${hasta})`
    window.location.href = `mailto:?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(consultaTexto())}`
  }

  return (
    <div>
      <div style={st.headerRow}>
        <div>
          <h2 style={st.title}>Historia de clientes</h2>
          <p style={st.subtitle}>
            {vista === 'crm'
              ? 'Todo lo que pasó con el cliente para evaluar su cuenta: ventas, entregas, cobros con problema, opiniones, casos, WhatsApp, mails y campañas. Los números de compra salen de la historia de Presea.'
              : 'Cuenta corriente del cliente, leída en vivo de Presea. Click en una fila para ver el detalle.'}
          </p>
        </div>
      </div>

      <div style={st.solapas}>
        {[['presea', '💰 Cuenta corriente (Presea)'], ['crm', '📋 Historial (CRM)']].map(([k, label]) => (
          <button key={k} type="button"
            style={{ ...sx.pill, ...(vista === k ? sx.pillActive : {}) }}
            onClick={() => {
              setVista(k)
              if (cliente && (k === 'crm' || !resp)) consultar(cliente, desde, hasta, k)
            }}>{label}</button>
        ))}
      </div>

      <div style={{ ...sx.card, padding: space.xl }}>
        {/* Cliente: se escribe directo (número, razón social, nombre de
            fantasía o CUIT — Martin, 5 oct 2026) o se busca con la lupa. */}
        <label style={st.label}>Cliente</label>
        <div style={st.clienteRow}>
          <div style={{ flex: 1 }}>
            <BuscadorCliente value={cliente} onChange={elegirCliente}
              placeholder="Número, razón social, nombre de fantasía o CUIT…" />
          </div>
          <Boton variante="secondary" onClick={() => setPickerAbierto(true)}>🔍 Buscar cliente</Boton>
        </div>
        {vista === 'presea' && cliente && !cliente.codigo_presea && (
          <div style={st.aviso}>⚠ Este cliente no tiene código Presea — la consulta no va a traer nada.</div>
        )}

        {/* Presets de fecha */}
        <div style={st.presetRow}>
          {PRESETS.map(p => (
            <button key={p.key}
              style={{ ...sx.pill, ...(presetActivo === p.key ? sx.pillActive : {}) }}
              onClick={() => aplicarPreset(p.key)}>{p.label}</button>
          ))}
        </div>

        <div style={st.formGrid}>
          <div>
            <label style={st.label}>Fecha desde</label>
            {/* Campo nativo: se edita por segmento (día/mes/año) + calendario,
                sin la máscara que corría los dígitos al editar el medio. */}
            <input type="date" style={st.inputDate}
              value={fechaToISO(desde)} onChange={e => { setDesde(isoToFecha(e.target.value)); setPresetActivo(null) }} />
          </div>
          <div>
            <label style={st.label}>Fecha hasta</label>
            <input type="date" style={st.inputDate}
              value={fechaToISO(hasta)} onChange={e => { setHasta(isoToFecha(e.target.value)); setPresetActivo(null) }} />
          </div>
          <div style={st.btnRow}>
            <Boton variante="primary" onClick={() => consultar()} disabled={loading}>
              {loading ? 'Consultando…' : 'Consultar'}
            </Boton>
          </div>
        </div>
        {error && <div style={st.error}>{error}</div>}
      </div>

      {vista === 'crm' && <HistorialCrm cliente={cliente} desde={desde} hasta={hasta} consulta={consultaCrm} />}

      {/* Encabezado con datos del cliente */}
      {vista === 'presea' && resp && cliente && (
        <div style={{ ...sx.card, marginTop: space.xl, padding: space.xl }}>
          <div style={st.clienteHeader}>
            <div style={st.chCol}>
              <div style={st.chLbl}>Cliente</div>
              <div style={st.chVal}><strong>{cliente.nombre}</strong></div>
              <div style={st.chSub}><span style={sx.mono}>Cód. {cliente.codigo_presea}</span>{cliente.cuit ? <> · CUIT <span style={sx.mono}>{cliente.cuit}</span></> : ''}</div>
            </div>
            <div style={st.chCol}>
              <div style={st.chLbl}>Domicilio</div>
              <div style={st.chVal}>{cliente.domicilio || '—'}</div>
              <div style={st.chSub}>{[cliente.localidad, cliente.provincia].filter(Boolean).join(', ') || '—'}</div>
            </div>
            <div style={st.chCol}>
              <div style={st.chLbl}>Contacto</div>
              <div style={st.chVal}>{cliente.telefono || cliente.telefono_wa || '—'}</div>
              <div style={st.chSub}>{cliente.email || '—'}{cliente.lista_precios ? ` · Lista ${cliente.lista_precios}` : ''}</div>
            </div>
          </div>

          {/* KPIs de totales — saldo final destacado */}
          <div style={st.kpiRow}>
            <Kpi label="Saldo anterior" valor={totales.anterior} />
            <Kpi label="Debe" valor={totales.debe} />
            <Kpi label="Haber" valor={totales.haber} />
            <Kpi label="Saldo final" valor={totales.saldoFinal} destacado />
          </div>

          {/* Reporte + envío */}
          <div style={st.toolbar}>
            <span style={st.toolbarLbl}>Reporte</span>
            <Boton sm variante="secondary" onClick={() => bajarReporte('pdf')} disabled={bajando}>{bajando === 'pdf' ? '…' : '⬇ PDF'}</Boton>
            <Boton sm variante="secondary" onClick={() => bajarReporte('excel')} disabled={bajando}>{bajando === 'excel' ? '…' : '⬇ Excel'}</Boton>
            <Boton sm variante="secondary" onClick={() => bajarReporte('csv')} disabled={bajando}>{bajando === 'csv' ? '…' : '⬇ CSV'}</Boton>
            <span style={{ ...st.toolbarLbl, marginLeft: space.lg }}>Enviar</span>
            <Boton sm variante="send" onClick={enviarWhatsApp}>📱 WhatsApp</Boton>
            <Boton sm variante="send" onClick={enviarMail}>✉ Mail</Boton>
          </div>
        </div>
      )}

      {vista === 'presea' && resp && (
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden', marginTop: space.xl }}>
          <div style={st.resultHeader}>
            <span style={st.resultTitle}>
              {filas.length} {filas.length === 1 ? 'movimiento' : 'movimientos'}
              <span style={st.resultMeta}> · {desde} → {hasta}</span>
            </span>
          </div>

          {filas.length === 0 ? (
            <div style={st.empty}>Sin movimientos en ese rango.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={sx.table}>
                <thead>
                  <tr>
                    <th style={sx.th}>Fecha</th>
                    <th style={sx.th}>Concepto</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Comprobante</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Debe</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Haber</th>
                    <th style={{ ...sx.th, textAlign: 'right' }}>Saldo</th>
                    <th style={sx.th}></th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={st.trAnterior}>
                    <td style={sx.td}></td>
                    <td style={{ ...sx.td, fontStyle: 'italic', color: colors.textSec }}>Saldo anterior</td>
                    <td style={sx.td}></td>
                    <td style={sx.td}></td>
                    <td style={sx.td}></td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', color: colors.textSec }}>{fmtAR(totales.anterior)}</td>
                    <td style={sx.td}></td>
                  </tr>
                  {filasSaldo.map((f, i) => (
                    <tr key={i} style={f.es_remito ? st.trRemito : st.tr}>
                      <td style={{ ...sx.td, ...sx.mono }}>{f.fecha}</td>
                      <td style={sx.td}>
                        {f.concepto}
                        {f.es_remito && <span style={st.noSuma}>no suma</span>}
                      </td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{f.nro_comprobante || '—'}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{f.debe ? fmtAR(f.debe) : ''}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{f.haber ? fmtAR(f.haber) : ''}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 600 }}>{f.es_remito ? '' : fmtAR(f.saldo)}</td>
                      <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {/* Sug #55: descarga directa del PDF cuando el comprobante
                            existe en LGR (facturado desde acá). Las filas
                            históricas de Presea no tienen documento → sin botón. */}
                        {f.pdf && (
                          <Boton sm variante="secondary" title={`Descargar el ${f.pdf.tipo} en PDF`}
                            onClick={() => descargarArchivo(
                              f.pdf.tipo === 'factura' ? `/notas-venta/${f.pdf.id}/factura-fiscal`
                                : f.pdf.tipo === 'recibo' ? `/pagos/${f.pdf.id}/pdf`
                                : `/remitos/${f.pdf.id}/pdf`,
                              `${f.pdf.tipo}_${f.nro_comprobante}.pdf`
                            )}>
                            ⬇ PDF
                          </Boton>
                        )}{' '}
                        <Boton sm variante="ghost" onClick={() => abrirDetalle(f)}>Ver →</Boton>
                      </td>
                    </tr>
                  ))}
                  <tr style={st.trTotales}>
                    <td style={st.tdTot}></td>
                    <td style={st.tdTot}>TOTALES</td>
                    <td style={st.tdTot}></td>
                    <td style={{ ...st.tdTot, ...sx.mono, textAlign: 'right' }}>{fmtAR(totales.debe)}</td>
                    <td style={{ ...st.tdTot, ...sx.mono, textAlign: 'right' }}>{fmtAR(totales.haber)}</td>
                    <td style={{ ...st.tdTot, ...sx.mono, textAlign: 'right' }}>{fmtAR(totales.saldoFinal)}</td>
                    <td style={st.tdTot}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pickerAbierto && (
        <ModalListaClientes onSelect={elegirCliente} onClose={() => setPickerAbierto(false)} />
      )}

      {detalle && (
        <Modal onClose={() => setDetalle(null)} title={`Detalle · ${detalle.fila.concepto} ${detalle.fila.nro_comprobante || ''}`} maxWidth="900px">
          {/* Cabecera de la factura: cliente, comprobante y total */}
          {(() => {
            const cab = cabeceraFactura(detalle)
            return (
              <div style={st.facCab}>
                <div style={st.facCabGrid}>
                  <div>
                    <div style={st.chLbl}>Cliente</div>
                    <div style={st.chVal}><strong>{cliente?.nombre || '—'}</strong></div>
                    <div style={st.chSub}>
                      <span style={sx.mono}>Cód. {cliente?.codigo_presea}</span>
                      {cliente?.cuit ? <> · CUIT <span style={sx.mono}>{cliente.cuit}</span></> : ''}
                    </div>
                  </div>
                  <div>
                    <div style={st.chLbl}>Comprobante</div>
                    <div style={st.chVal}>{detalle.fila.concepto}</div>
                    <div style={st.chSub}>
                      <span style={sx.mono}>{detalle.fila.nro_comprobante || '—'}</span> · <span style={sx.mono}>{detalle.fila.fecha}</span>
                    </div>
                  </div>
                  <div style={st.facTotalBox}>
                    <div style={st.chLbl}>Total</div>
                    <div style={st.facTotal}>$ {fmtAR(cab.total)}</div>
                  </div>
                </div>
                {cab.tieneDesglose && (
                  <div style={st.facDesglose}>
                    <span>Neto <strong style={sx.mono}>{fmtAR(cab.neto)}</strong></span>
                    {cab.descuento > 0 && <span>Descuento <strong style={sx.mono}>{fmtAR(cab.descuento)}</strong></span>}
                    <span>IVA <strong style={sx.mono}>{fmtAR(cab.iva)}</strong></span>
                  </div>
                )}
                {detalle.cabecera && (
                  <div style={st.facDesglose}>
                    <span>Nota de venta <strong style={sx.mono}>{detalle.cabecera.notas_venta.join(', ') || '—'}</strong></span>
                    <span>Lista <strong style={sx.mono}>{detalle.cabecera.listas.join(', ') || '—'}</strong></span>
                    <span>Versión <strong style={sx.mono}>{detalle.cabecera.versiones.join(', ') || '—'}</strong></span>
                  </div>
                )}
              </div>
            )
          })()}
          {detalle.loading ? (
            <div style={st.empty}>Cargando…</div>
          ) : detalle.error ? (
            <div style={st.empty}>{detalle.error}</div>
          ) : detalle.recibo ? (
            <ReciboDetalle recibo={detalle.recibo} />
          ) : detalle.renglones.length === 0 ? (
            <div style={st.empty}>Este comprobante no tiene detalle (cobranza/saldo/ajuste).</div>
          ) : (
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>Cód. int.</th>
                  <th style={sx.th}>Código</th>
                  <th style={sx.th}>Descripción</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Cant.</th>
                  <th style={{ ...sx.th, textAlign: 'right' }} title="Precio de lista con IVA × cantidad, antes del descuento">Precio × cant.</th>
                  <th style={{ ...sx.th, textAlign: 'right' }} title="Precio unitario final, con IVA y descuento">Precio</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {detalle.renglones.map((m, i) => (
                  <tr key={i} style={st.tr}>
                    <td style={{ ...sx.td, ...sx.mono }}>{m.codigo || '—'}</td>
                    <td style={{ ...sx.td, ...sx.mono }}>{m.cod_alfa || '—'}</td>
                    <td style={sx.td}>{m.descripcion}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(m.cantidad)}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(m.precio_por_cantidad)}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(m.precio_unitario)}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(m.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  )
}

// Detalle de un recibo (HistoriaClientePreseaService::recibo): importe y
// retenciones de Presea; valores e imputaciones solo si el recibo salió de LGR
// (de los hechos en Presea, DETALLE_COMPRO no trae ni valores ni imputaciones).
function ReciboDetalle({ recibo }) {
  const { presea, lgr } = recibo
  if (!presea && !lgr) return <div style={st.empty}>Presea no devolvió datos de este recibo.</div>
  return (
    <div>
      {presea && (
        <div style={st.reciboBloque}>
          <div style={st.chLbl}>Importe y retenciones (Presea)</div>
          <table style={sx.table}>
            <tbody>
              <tr style={st.tr}>
                <td style={sx.td}>Importe del recibo</td>
                <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(presea.importe)}</td>
              </tr>
              {presea.retenciones.map(r => (
                <tr key={r.tipo} style={st.tr}>
                  <td style={sx.td}>Retención {r.label}</td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>− {fmtAR(r.importe)}</td>
                </tr>
              ))}
              <tr style={st.tr}>
                <td style={{ ...sx.td, fontWeight: 600 }}>{presea.retenciones.length ? 'Neto recibido' : 'Sin retenciones'}</td>
                <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 600 }}>{fmtAR(presea.neto)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {lgr ? (
        <div style={st.reciboBloque}>
          <div style={st.chLbl}>Cobro cargado en LGR</div>
          <div style={st.facDesglose}>
            <span>Forma de pago <strong>{lgr.medio || '—'}</strong></span>
            {lgr.referencia && <span>Referencia <strong style={sx.mono}>{lgr.referencia}</strong></span>}
            {lgr.cuotas && <span>Cuotas <strong style={sx.mono}>{lgr.cuotas}</strong></span>}
          </div>
          {lgr.valores.length > 0 && (
            <table style={{ ...sx.table, marginTop: space.md }}>
              <thead>
                <tr>
                  <th style={sx.th}>Valor</th>
                  <th style={sx.th}>Número</th>
                  <th style={sx.th}>Banco</th>
                  <th style={sx.th}>Fecha</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {lgr.valores.map((v, i) => (
                  <tr key={i} style={st.tr}>
                    <td style={sx.td}>{v.tipo}</td>
                    <td style={{ ...sx.td, ...sx.mono }}>{v.numero || '—'}</td>
                    <td style={sx.td}>{v.banco || '—'}</td>
                    <td style={{ ...sx.td, ...sx.mono }}>{v.fecha || '—'}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(v.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {lgr.imputados.length > 0 && (
            <table style={{ ...sx.table, marginTop: space.md }}>
              <thead>
                <tr>
                  <th style={sx.th}>Imputado a</th>
                  <th style={sx.th}>Factura</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {lgr.imputados.map(i => (
                  <tr key={i.nota_venta_id} style={st.tr}>
                    <td style={sx.td}><Link to={`/notas-venta/${i.nota_venta_id}`}>{i.nv_lgr}</Link></td>
                    <td style={{ ...sx.td, ...sx.mono }}>{i.factura || '—'}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtAR(i.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {lgr.observaciones && <div style={st.reciboObs}>{lgr.observaciones}</div>}
        </div>
      ) : (
        <div style={st.reciboObs}>Recibo hecho en Presea: los valores (efectivo, cheque, transferencia) y los comprobantes que canceló no los informa Presea.</div>
      )}
    </div>
  )
}

function Kpi({ label, valor, destacado }) {
  return (
    <div style={{ ...st.kpi, ...(destacado ? st.kpiDestacado : {}) }}>
      <div style={st.kpiLbl}>{label}</div>
      <div style={{ ...st.kpiVal, ...(destacado ? st.kpiValDestacado : {}) }}>$ {fmtAR(valor)}</div>
    </div>
  )
}

// Los totales los calcula el backend (HistoriaClientePreseaService::renglones);
// sin renglones (recibo, ajuste) el total es el de la fila de la historia.
function cabeceraFactura(detalle) {
  const t = detalle?.totales
  const fila = detalle?.fila || {}
  const conRenglones = (detalle?.renglones || []).length > 0
  return {
    neto: t?.neto || 0,
    iva: t?.iva || 0,
    descuento: t?.descuento || 0,
    total: (conRenglones && t?.total) || Number(fila.debe || 0) || Number(fila.haber || 0),
    tieneDesglose: conRenglones,
  }
}

// Acumula el saldo fila a fila y arma los totales financieros (sin remitos).
function computarSaldos(resp) {
  const anterior = Number(resp?.saldo_anterior || 0)
  let saldo = anterior
  let debe = 0, haber = 0
  const filasSaldo = (resp?.filas || []).map(f => {
    if (f.es_remito) return { ...f, saldo: null }
    saldo += (f.debe || 0) - (f.haber || 0)
    debe += f.debe || 0
    haber += f.haber || 0
    return { ...f, saldo }
  })
  return { filasSaldo, totales: { debe, haber, saldoFinal: saldo, anterior } }
}

const PRESETS = [
  { key: 'todos', label: 'Todos los tiempos' },
  { key: '30', label: 'Últimos 30 días' },
  { key: '90', label: 'Últimos 90 días' },
  { key: 'actual', label: 'Año actual' },
  { key: 'anterior', label: 'Año anterior' },
]

function rangoPreset(key) {
  const hoy = new Date()
  if (key === 'todos') return { d: '01/01/2000', h: fmtFecha(hoy) }
  if (key === '30' || key === '90') {
    const d = new Date(); d.setDate(d.getDate() - Number(key))
    return { d: fmtFecha(d), h: fmtFecha(hoy) }
  }
  if (key === 'actual') return { d: `01/01/${hoy.getFullYear()}`, h: fmtFecha(hoy) }
  // anterior
  const y = hoy.getFullYear() - 1
  return { d: `01/01/${y}`, h: `31/12/${y}` }
}

// Conversores DD/MM/AAAA ↔ ISO (YYYY-MM-DD) para el campo de fecha nativo.
function fechaToISO(s) { const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || ''); return m ? `${m[3]}-${m[2]}-${m[1]}` : '' }
function isoToFecha(s) { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? `${m[3]}/${m[2]}/${m[1]}` : '' }

function defaultDesde() { const d = new Date(); d.setMonth(d.getMonth() - 3); return fmtFecha(d) }
function defaultHasta() { return fmtFecha(new Date()) }
function fmtFecha(d) { return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` }
function validFecha(s) { return /^\d{2}\/\d{2}\/\d{4}$/.test(s || '') }
function fmtAR(n) { return (Number(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const st = {
  solapas: { display: 'flex', gap: space.sm, marginBottom: space.lg },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  subtitle: { margin: '4px 0 0', fontSize: text.body, color: colors.textSec, maxWidth: 760, lineHeight: 1.5 },
  label: { display: 'block', fontSize: text.meta, fontWeight: 600, color: colors.textSec, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' },
  clienteRow: { display: 'flex', gap: space.lg, alignItems: 'center', marginBottom: space.lg },
  presetRow: { display: 'flex', gap: space.sm, flexWrap: 'wrap', marginBottom: space.lg },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: space.lg, alignItems: 'end' },
  input: { width: '100%', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.mono, outline: 'none', boxSizing: 'border-box' },
  inputDate: { width: '100%', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.mono, color: colors.text, outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
  btnRow: { display: 'flex', justifyContent: 'flex-end' },
  aviso: { marginTop: 6, marginBottom: space.lg, fontSize: text.sec, color: colors.warn },
  error: { marginTop: space.lg, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, background: colors.errBg, color: colors.err, fontSize: text.sec, fontWeight: 500 },

  clienteHeader: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: space.xl, paddingBottom: space.lg, borderBottom: `1px solid ${colors.borderSoft}` },
  chCol: {},
  chLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 },
  chVal: { fontSize: text.body, color: colors.text },
  chSub: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },

  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: space.lg, marginTop: space.lg },
  kpi: { padding: `${space.lg}px ${space.xl}px`, border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft },
  kpiDestacado: { background: colors.linkBg, border: `1px solid ${colors.link}` },
  kpiLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  kpiVal: { fontSize: text.title, fontWeight: 600, color: colors.text, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 4 },
  kpiValDestacado: { color: colors.link, fontSize: text.display, fontWeight: 700 },

  toolbar: { display: 'flex', alignItems: 'center', gap: space.sm, marginTop: space.xl, flexWrap: 'wrap' },
  toolbarLbl: { fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2 },

  resultHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.border}` },
  resultTitle: { fontSize: text.body, fontWeight: 600, color: colors.text },
  resultMeta: { fontWeight: 400, color: colors.textSec, fontFamily: font.mono },
  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  trRemito: { height: 44, borderBottom: `1px solid ${colors.borderSoft}`, color: colors.textMeta, background: colors.bgSoft },
  trAnterior: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  noSuma: { ...sx.badge, marginLeft: 6, color: colors.textSec, background: colors.bgSoft, border: `1px solid ${colors.border}`, fontWeight: 500 },
  trTotales: { borderTop: `2px solid ${colors.borderStrong}` },
  tdTot: { padding: `${space.lg}px ${space.lg}px`, fontWeight: 700, fontSize: text.body, color: colors.text },
  empty: { padding: `${space.xl}px`, textAlign: 'center', color: colors.textMeta, fontSize: text.body },

  reciboBloque: { marginBottom: space.lg },
  reciboObs: { marginTop: space.md, fontSize: text.sec, color: colors.textMeta },
  facCab: { border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bgSoft, padding: space.lg, marginBottom: space.lg },
  facCabGrid: { display: 'grid', gridTemplateColumns: '1.4fr 1fr auto', gap: space.xl, alignItems: 'start' },
  facTotalBox: { textAlign: 'right', minWidth: 150 },
  facTotal: { fontSize: text.display, fontWeight: 700, color: colors.link, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 2 },
  facDesglose: { display: 'flex', gap: space.xl, marginTop: space.lg, paddingTop: space.md, borderTop: `1px solid ${colors.border}`, fontSize: text.sec, color: colors.textSec },
}
